/**
 * useByokKeys — React Query wrappers around the BYOK metadata table + RPCs.
 *
 * Read path: SELECT against `byok_keys` directly (RLS scopes to owner).
 * Write path: always RPC, never direct INSERT/DELETE — the RPCs handle the
 * vault side of the operation atomically.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type ByokKeyRow =
  Database["public"]["Tables"]["byok_keys"]["Row"];
export type ByokProviderEnum =
  Database["public"]["Enums"]["byok_provider"];

const QUERY_KEY = ["byok-keys"] as const;

export function useByokKeys() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<ByokKeyRow[]> => {
      const { data, error } = await supabase
        .from("byok_keys")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useStoreByokKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      provider: ByokProviderEnum;
      apiKey: string;
      label: string;
    }) => {
      const { data, error } = await supabase.rpc("byok_store_key", {
        p_provider: input.provider,
        p_api_key: input.apiKey,
        p_label: input.label,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteByokKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc("byok_delete_key", { p_id: id });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
